/**
 * PresentialPayment Page
 * 
 * Payment page for presential courses accessed via unique token link.
 * Displays pre-filled student data (read-only) and allows WebPay payment.
 * 
 * Route: /pago-presencial/:token
 * Requirements: 14.3, 14.4
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle,
  CreditCard,
  User,
  Mail,
  Phone,
  FileText,
  MapPin,
  Calendar,
  ArrowLeft,
  ShieldCheck
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getPaymentData, createPresentialPayment, redirectToWebPay } from '../services/presentialPaymentApi';
import { formatCLP } from '../utils/priceFormat';

/**
 * PresentialPayment Component
 * 
 * Handles the payment flow for presential courses:
 * 1. Fetches payment data using token from URL
 * 2. Displays pre-filled student information (read-only)
 * 3. Shows course details and price
 * 4. Allows payment via WebPay
 */
export default function PresentialPayment() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isAlreadyPaid, setIsAlreadyPaid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch payment data on mount
  useEffect(() => {
    async function fetchPaymentData() {
      if (!token) {
        setError('Token de pago no proporcionado.');
        setIsLoading(false);
        return;
      }

      try {
        const data = await getPaymentData(token);
        setPaymentData(data);
      } catch (err) {
        console.error('Error fetching payment data:', err);
        setError(err.message);
        
        if (err.expired || err.status === 410) {
          setIsExpired(true);
        }
        if (err.alreadyPaid) {
          setIsAlreadyPaid(true);
        }
      } finally {
        setIsLoading(false);
      }
    }

    fetchPaymentData();
  }, [token]);

  /**
   * Handle payment button click
   * Creates WebPay transaction and redirects to payment form
   */
  const handlePayment = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setError(null);

    try {
      const result = await createPresentialPayment(token);
      
      if (result.success && result.url && result.token) {
        // Redirect to WebPay
        redirectToWebPay(result.url, result.token);
      } else {
        throw new Error('Error al crear la transacción de pago.');
      }
    } catch (err) {
      console.error('Error creating payment:', err);
      setError(err.message);
      setIsProcessing(false);
      
      if (err.expired) {
        setIsExpired(true);
      }
      if (err.alreadyPaid) {
        setIsAlreadyPaid(true);
      }
    }
  };

  /**
   * Navigate to home page
   */
  const handleGoHome = () => {
    navigate('/');
  };

  /**
   * Format expiration date
   */
  const formatExpirationDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Header />
      <section className="min-h-screen py-24 bg-slate-900 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-cyan-500/20 via-transparent to-transparent" />
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto">
            {/* Loading State */}
            {isLoading && (
              <LoadingState />
            )}

            {/* Error States */}
            {!isLoading && error && (
              <>
                {isExpired ? (
                  <ExpiredState onGoHome={handleGoHome} />
                ) : isAlreadyPaid ? (
                  <AlreadyPaidState onGoHome={handleGoHome} />
                ) : (
                  <ErrorState message={error} onGoHome={handleGoHome} />
                )}
              </>
            )}

            {/* Payment Form */}
            {!isLoading && !error && paymentData && (
              <PaymentForm
                data={paymentData}
                onPayment={handlePayment}
                onGoHome={handleGoHome}
                isProcessing={isProcessing}
                formatExpirationDate={formatExpirationDate}
              />
            )}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

/**
 * Loading state component
 */
function LoadingState() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/20"
    >
      <div className="text-center py-12">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
          <Loader2 className="w-10 h-10 text-white animate-spin" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">
          Cargando datos de pago
        </h2>
        <p className="text-slate-400 max-w-md mx-auto">
          Por favor espera mientras verificamos tu enlace de pago...
        </p>
      </div>
    </motion.div>
  );
}

/**
 * Expired link state component
 */
function ExpiredState({ onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/20"
    >
      <div className="text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
          <Clock className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
          Enlace Expirado
        </h2>
        <p className="text-slate-400 mb-8 max-w-md mx-auto">
          Este enlace de pago ha expirado. Por favor, contacta al Instituto Valparaíso para obtener un nuevo enlace.
        </p>
        
        <div className="bg-white/5 rounded-xl p-4 mb-8">
          <p className="text-slate-300 text-sm">
            📧 contacto@institutovalparaiso.cl<br />
            📞 +56 9 XXXX XXXX
          </p>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          className="flex items-center justify-center gap-2 px-6 py-3 mx-auto bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al Inicio
        </motion.button>
      </div>
    </motion.div>
  );
}

/**
 * Already paid state component
 */
function AlreadyPaidState({ onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/20"
    >
      <div className="text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
          Pago Ya Realizado
        </h2>
        <p className="text-slate-400 mb-8 max-w-md mx-auto">
          Este curso ya ha sido pagado. Revisa tu correo electrónico para ver los detalles de confirmación.
        </p>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          className="flex items-center justify-center gap-2 px-6 py-3 mx-auto bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al Inicio
        </motion.button>
      </div>
    </motion.div>
  );
}

/**
 * Generic error state component
 */
function ErrorState({ message, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/20"
    >
      <div className="text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/30">
          <AlertCircle className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
          Error
        </h2>
        <p className="text-slate-400 mb-8 max-w-md mx-auto">
          {message}
        </p>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          className="flex items-center justify-center gap-2 px-6 py-3 mx-auto bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al Inicio
        </motion.button>
      </div>
    </motion.div>
  );
}


/**
 * Payment form component with pre-filled data
 * Requirements: 14.3, 14.4
 */
function PaymentForm({ data, onPayment, onGoHome, isProcessing, formatExpirationDate }) {
  const {
    studentName,
    email,
    rut,
    phone,
    courseName,
    coursePrice,
    courseDate,
    courseLocation,
    expiresAt
  } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/20"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
          <CreditCard className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Pago de Curso Presencial
        </h1>
        <p className="text-slate-400">
          Instituto Valparaíso
        </p>
      </div>

      {/* Course Info Card */}
      <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-6 mb-6 border border-blue-400/20">
        <h2 className="text-xl font-semibold text-white mb-4">{courseName}</h2>
        
        <div className="space-y-3">
          {courseDate && (
            <div className="flex items-center gap-3 text-slate-300">
              <Calendar className="w-5 h-5 text-cyan-400 flex-shrink-0" />
              <span>Fecha: {courseDate}</span>
            </div>
          )}
          {courseLocation && (
            <div className="flex items-center gap-3 text-slate-300">
              <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0" />
              <span>Lugar: {courseLocation}</span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex justify-between items-center">
            <span className="text-slate-400">Total a pagar:</span>
            <span className="text-2xl font-bold text-white">{formatCLP(coursePrice)}</span>
          </div>
        </div>
      </div>

      {/* Student Info (Read-only) */}
      <div className="bg-white/5 rounded-2xl p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Datos del Estudiante</h3>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Nombre</p>
              <p className="text-white">{studentName}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Email</p>
              <p className="text-white">{email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">RUT</p>
              <p className="text-white">{rut}</p>
            </div>
          </div>

          {phone && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Teléfono</p>
                <p className="text-white">{phone}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expiration Warning */}
      {expiresAt && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 text-sm font-medium">Enlace válido hasta:</p>
              <p className="text-amber-200 text-sm">{formatExpirationDate(expiresAt)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 mb-6 text-slate-400 text-sm">
        <ShieldCheck className="w-4 h-4" />
        <span>Pago seguro con WebPay</span>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <motion.button
          whileHover={{ scale: isProcessing ? 1 : 1.02 }}
          whileTap={{ scale: isProcessing ? 1 : 0.98 }}
          onClick={onPayment}
          disabled={isProcessing}
          className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all ${
            isProcessing ? 'opacity-70 cursor-not-allowed' : ''
          }`}
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Procesando...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Pagar {formatCLP(coursePrice)}
            </>
          )}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          disabled={isProcessing}
          className="flex items-center justify-center gap-2 px-6 py-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Cancelar
        </motion.button>
      </div>

      {/* Footer Note */}
      <p className="text-center text-slate-500 text-xs mt-6">
        Al hacer clic en "Pagar", serás redirigido a WebPay para completar tu pago de forma segura.
      </p>
    </motion.div>
  );
}
